import React from 'react'

import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import momentTz from 'moment-timezone'

import { GlobalNotification } from './global-components/GlobalNotification'

const IMPORT_DEFAULT_PATH = '/services/plugin-static-data-manager/import'
const IMPORT_DEFAULT_HEADERS = { 'Content-Type': 'application/json' }

export function saveConfigFile(configName: string, content: string): Promise<any> {
    return fetch(`${IMPORT_DEFAULT_PATH}/configfile`, {
        method: 'POST',
        headers: IMPORT_DEFAULT_HEADERS,
        body: JSON.stringify({
            configName: configName,
            json: content
        })
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification
                        title={`${i18n('Unable to save config')} (${configName})`}
                        description={res.body?.message}
                        type="error"
                    />
                )

                return res
            }

            return res.body
        })
}

export function fetchConfigFiles(): Promise<Record<string, ConfigFile>> {
    return fetch(`${IMPORT_DEFAULT_PATH}/configfile`, {
        method: 'GET',
        headers: IMPORT_DEFAULT_HEADERS
    }).then(r => r.json())
}

export function fetchAllTemporaryData(): Promise<TemporaryData[]> {
    return fetch(`${IMPORT_DEFAULT_PATH}/temporarydata`, {
        method: 'GET',
        headers: IMPORT_DEFAULT_HEADERS
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification
                        title={`${i18n('Unable to load data')}`}
                        description={res.body?.message}
                        type="error"
                    />
                )

                return res
            }

            return res.body
        })
}

export function saveAllTemporaryData(row: any, entityType: string): Promise<TemporaryData[]> {
    const data: TemporaryData[] = [
        {
            id: row.rowId?.toString(),
            parent: row.parent.toString(),
            nodeId: row.nodeId.toString(),
            entityType: entityType,
            content: JSON.stringify(row)
        }
    ]

    return fetch(`${IMPORT_DEFAULT_PATH}/temporarydata`, {
        method: 'POST',
        headers: IMPORT_DEFAULT_HEADERS,
        body: JSON.stringify(data)
    }).then(r => r.json())
}

export function deleteNodeAndItsChildren(rowId: number): Promise<number> {
    return fetch(`${IMPORT_DEFAULT_PATH}/temporarydata/${rowId}`, {
        method: 'DELETE',
        headers: IMPORT_DEFAULT_HEADERS
    }).then(r => r.status)
}

export function importStaticTemporaryData(configName: string, file, timezone: string): Promise<any> {
    const formData = new FormData()
    formData.append('configName', configName)
    formData.append('file', file)
    formData.append('timezone', timezone)

    return fetch(`${IMPORT_DEFAULT_PATH}/csv`, {
        method: 'POST',
        body: formData
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification
                        title="An error occurred when importing the data"
                        description={res.body?.message}
                        type="error"
                    />
                )

                return res
            }

            return res.body
        })
        .catch(error => {
            console.error('error:', error)
            globalNotification.error(
                <GlobalNotification title="An error occurred when importing the data" type="error" />,
                10
            )
        })
}

export function validateLocalFieldsByEntity(entitiesMap: any): Promise<Record<string, Array<Record<string, string>>>> {
    return fetch(`${IMPORT_DEFAULT_PATH}/validatelocal`, {
        method: 'POST',
        headers: IMPORT_DEFAULT_HEADERS,
        body: JSON.stringify(entitiesMap)
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification
                        title="Error validating the fields"
                        description={res.body?.message}
                        type="error"
                    />
                )

                return res
            }

            return res.body
        })
        .catch(error => {
            console.error('error:', error)
            globalNotification.error(<GlobalNotification title="Error validating the fields" type="error" />, 10)
        })
}

export function validateEntityByHierarchy(entitiesMap: any): Promise<Record<string, string>> {
    return fetch(`/services/plugin-static-data-manager/intervention/validatehierarchical`, {
        method: 'POST',
        headers: IMPORT_DEFAULT_HEADERS,
        body: JSON.stringify({ ...entitiesMap, timeZone: momentTz.tz.guess() })
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification title="Error validating the entities" description={res.body?.message} />
                )

                return res
            }

            return res.body
        })
        .catch(error => {
            console.error('error:', error)
            globalNotification.error(<GlobalNotification title="Error validating the entities" type="error" />)
        })
}

export function transferDraftTableToStaticDataDashboard(entitiesMap: any): Promise<Record<string, string>> {
    return fetch(`/services/plugin-static-data-manager/intervention/exportstaticdata`, {
        method: 'POST',
        headers: IMPORT_DEFAULT_HEADERS,
        body: JSON.stringify(entitiesMap)
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <GlobalNotification title="Error transferring the entities" description={res.body?.message} />
                )

                return res
            }

            return res.body
        })
        .catch(error => {
            console.error('error:', error)
            globalNotification.error(<GlobalNotification title="Error transferring the entities" type="error" />)
        })
}

export const fetchKpiGoalsWithFilter: (filter: any, rigId?: string) => Promise<any> = (filter, rigId = 'GLOBAL') => {
    return fetch(`/services/plugin-kpis-drilling/config/kpi_goals/${rigId}/filter`, {
        method: 'POST',
        body: JSON.stringify(filter),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(r => r?.json().then(data => ({ status: r?.status, body: data })))
        .then(res => {
            return res?.body ?? []
        })
        .catch(err => {
            console.error(err)
            globalNotification.error(i18n(`Unable to fetch kpi goals with filter.`))

            return []
        })
}
